import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ordersAPI, customersAPI, measurementsAPI } from '../services/api';
import { FiArrowLeft } from 'react-icons/fi';
import { useAlert } from '../context/AlertContext';

const AddOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get('customer_id');
  
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const alert = useAlert();
  const [formData, setFormData] = useState({
    customer_id: customerIdFromUrl || '',
    measurement_id: '',
    garment_type: 'Shirt',
    quantity: 1,
    order_date: new Date().toISOString().split('T')[0],
    delivery_date: '',
    status: 'pending',
    price: '',
    advance_payment: 0,
    notes: '',
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }, []);

  const fetchMeasurements = useCallback(async (customerId) => {
    try {
      const response = await measurementsAPI.getAll(customerId);
      setMeasurements(response.data);
    } catch (error) {
      console.error('Error fetching measurements:', error);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (formData.customer_id) {
      fetchMeasurements(formData.customer_id);
    }
  }, [formData.customer_id, fetchMeasurements]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.garment_type) {
      alert.push({ type: 'warning', title: 'Missing data', message: 'Customer and garment type are required' });
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        order_date: formData.order_date ? new Date(formData.order_date).toISOString() : new Date().toISOString(),
        delivery_date: formData.delivery_date ? new Date(formData.delivery_date).toISOString() : null,
        price: formData.price ? parseFloat(formData.price) : null,
        advance_payment: formData.advance_payment ? parseFloat(formData.advance_payment) : 0,
        measurement_id: formData.measurement_id || null,
      };
      await ordersAPI.create(submitData);
      alert.push({ type: 'success', title: 'Order created' });
      navigate(customerIdFromUrl ? `/customers/${customerIdFromUrl}` : '/orders');
    } catch (error) {
      console.error('Error creating order:', error);
      alert.push({ type: 'error', title: 'Create failed', message: 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const garmentTypes = ['Shirt', 'Pant', 'Suit', 'Blazer', 'Kurta', 'Sherwani', 'Dress', 'Other'];
  const statusOptions = ['pending', 'in_progress', 'completed', 'cancelled'];

  return (
    <div className="max-w-2xl mx-auto">
      <button
        onClick={() => navigate(customerIdFromUrl ? `/customers/${customerIdFromUrl}` : '/orders')}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-6"
      >
        <FiArrowLeft size={20} />
        <span>Back</span>
      </button>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Create New Order</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer <span className="text-red-500">*</span>
              </label>
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                required
                disabled={!!customerIdFromUrl}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Garment Type <span className="text-red-500">*</span>
              </label>
              <select
                name="garment_type"
                value={formData.garment_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {garmentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Measurement (Optional)
              </label>
              <select
                name="measurement_id"
                value={formData.measurement_id}
                onChange={handleChange}
                disabled={!formData.customer_id}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select Measurement</option>
                {measurements.map((measurement) => (
                  <option key={measurement.id} value={measurement.id}>
                    {measurement.garment_type} - {new Date(measurement.measurement_date).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantity
              </label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Order Date
              </label>
              <input
                type="date"
                name="order_date"
                value={formData.order_date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Delivery Date
              </label>
              <input
                type="date"
                name="delivery_date"
                value={formData.delivery_date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status.replace('_', ' ').toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price ($)
              </label>
              <input
                type="number"
                step="0.01"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Advance Payment ($)
              </label>
              <input
                type="number"
                step="0.01"
                name="advance_payment"
                value={formData.advance_payment}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Any additional notes about the order"
            />
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Order'}
            </button>
            <button
              type="button"
              onClick={() => navigate(customerIdFromUrl ? `/customers/${customerIdFromUrl}` : '/orders')}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOrder;

